/** The ground between the buildings: streets along the stands, roads between them.
 *
 * A town screen whose every building stands on its own contact shadow reads
 * as sprites laid on a painting, however good the painting - the critic's
 * last open tell (2026-09-04) was exactly that no way led anywhere. The classic
 * stitches every dwelling to the keep with a road, a stair or a quay, and
 * the eye reads the ground plane first and the buildings standing on it
 * second.
 *
 * Two kinds of way, both in canvas coordinates and both pure data:
 *
 *  - a STREET per stand (townLayouts `stands`): a ribbon along the stand's
 *    own ground curve, dropped a little below the feet of the buildings that
 *    stand on it so their contact shadows land on it, and spanning only the
 *    slots that are BUILT - an empty stand has no street, so the town's
 *    circulation grows with the town rather than describing one that is not
 *    there yet;
 *  - the ROADS the layout carries (townLayouts `roads`): polylines read off
 *    each painting between its stands, on painted ground - a terrace ramp, a
 *    ledge, the ice - never across open water.
 *
 * The drawing (townTracks.tsx) is a soft, noise-edged ribbon in the ground's
 * own palette (`ground`), so on snow it is trampled snow and on basalt it is
 * ash: a darkening of what is there with a worn pale centre, not a texture
 * laid over the picture. Depth is in the width (a stand's scale) and in the
 * band, which the renderer hazes.
 *
 * Pure: no DOM, no React, no kernel rule module, no randomness.
 */

import type { FactionLayout, LayoutStand } from "./townLayouts.js";
import { TOWN_LAYOUTS } from "./townLayouts.js";
import type { TownSlot } from "./townSlots.js";
import { TOWN_CANVAS_H, TOWN_CANVAS_W } from "./townSlots.js";

export type TrackPoint = readonly [number, number];

export interface TrackWay {
  readonly kind: "street" | "road";
  readonly band: TownSlot["band"];
  /** Canvas polyline down the ribbon's centre. */
  readonly points: readonly TrackPoint[];
  /** Canvas width of the ribbon's bed. */
  readonly width: number;
}

export interface TrackPlan {
  readonly ground: string;
  readonly ways: readonly TrackWay[];
}

/** Ribbon width per unit of stand scale: a front street at 1.3 is ~29 canvas px. */
const STREET_WIDTH = 22;
/**
 * Where the street's centre runs relative to the stand's ground curve: a
 * little ABOVE the feet. The front row stands on the stage's bottom edge, so
 * a street in front of it is off the screen; a ribbon centred just above the
 * ground line runs under every building's apron and shows in the gaps
 * between them, which is the trodden ground of a row of houses.
 */
const STREET_DROP = -2;
/** The street runs this far past the outermost built slot's footprint. */
const STREET_OVERHANG = 18;
const ROAD_WIDTH_NEAR = 24;
const ROAD_WIDTH_FAR = 11;

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** The stand's ground curve at x, the way town_layout.stand_y reads it. */
export function standY(path: readonly TrackPoint[], x: number): number {
  const first = path[0];
  const last = path[path.length - 1];
  if (first === undefined || last === undefined) return 0;
  if (x <= first[0]) return first[1];
  for (let i = 0; i + 1 < path.length; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (a === undefined || b === undefined) continue;
    if (a[0] <= x && x <= b[0]) {
      const t = b[0] === a[0] ? 0 : (x - a[0]) / (b[0] - a[0]);
      return a[1] + (b[1] - a[1]) * t;
    }
  }
  return last[1];
}

/** A ground line's depth class, for a road that crosses the bands. */
export function bandAt(y: number): TownSlot["band"] {
  return y < 450 ? "back" : y < 620 ? "middle" : "front";
}

function street(
  stand: LayoutStand,
  layout: FactionLayout,
  occupied: ReadonlySet<string>,
): TrackWay | null {
  const here = layout.slots.filter((s) => occupied.has(s.name) && stand.slots.includes(s.name));
  if (here.length === 0) return null;
  const left = Math.min(...here.map((s) => s.x - (s.w * s.scale) / 2)) - STREET_OVERHANG;
  const right = Math.max(...here.map((s) => s.x + (s.w * s.scale) / 2)) + STREET_OVERHANG;
  const x0 = clamp(left, 0, TOWN_CANVAS_W);
  const x1 = clamp(right, 0, TOWN_CANVAS_W);
  const drop = STREET_DROP * stand.scale;
  const xs = [x0, ...stand.path.map((p) => p[0]).filter((x) => x > x0 && x < x1), x1];
  const points = xs.map((x): TrackPoint => [Math.round(x), Math.round(clamp(standY(stand.path, x) + drop, 0, TOWN_CANVAS_H))]);
  return { kind: "street", band: stand.band, points, width: Math.round(STREET_WIDTH * stand.scale) };
}

function road(points: readonly TrackPoint[]): TrackWay {
  const ys = points.map((p) => p[1]);
  const mid = (Math.min(...ys) + Math.max(...ys)) / 2;
  const t = clamp((mid - 300) / 400, 0, 1);
  return {
    kind: "road",
    band: bandAt(mid),
    points,
    width: Math.round(ROAD_WIDTH_FAR + (ROAD_WIDTH_NEAR - ROAD_WIDTH_FAR) * t),
  };
}

/**
 * The faction's tracks, given the names of the slots with something built.
 *
 * Roads first, then streets nearest-last, so a near street's bed is drawn
 * over the road it meets and the join reads as the road arriving at the
 * street rather than crossing it. A faction without its own layout has none.
 */
export function townTracks(faction: string, occupied: ReadonlySet<string>): TrackPlan {
  const layout = TOWN_LAYOUTS[faction];
  if (layout === undefined) return { ground: "earth", ways: [] };
  const ways: TrackWay[] = layout.roads.map(road);
  const streets = layout.stands
    .map((stand) => street(stand, layout, occupied))
    .filter((w): w is TrackWay => w !== null)
    .sort((a, b) => (a.points[0]?.[1] ?? 0) - (b.points[0]?.[1] ?? 0));
  ways.push(...streets);
  return { ground: layout.ground, ways };
}

/** The `points` attribute of a `<polyline>` for a way. */
export const wayPoints = (way: TrackWay): string =>
  way.points.map((p) => `${String(p[0])},${String(p[1])}`).join(" ");

/** How much of the ribbon's ink survives at each depth: the far ground is in the air. */
export const TRACK_DEPTH: Readonly<Record<TownSlot["band"], number>> = Object.freeze({
  back: 0.6,
  middle: 0.85,
  front: 1,
});

export interface TrackPalette {
  /** The bed: what the ground looks like trodden, as `r,g,b`. */
  readonly bed: string;
  readonly bedAlpha: number;
  /** The worn centre, paler where feet have polished it. */
  readonly worn: string;
  readonly wornAlpha: number;
}

/**
 * Per ground, what a track does to the painting. Earth is a pale worn dirt
 * band on darkened edges - The classic Castle's roads are lighter than its grass;
 * trampled snow goes grey-blue with only a faint centre; ash and mud are
 * dark with a dull centre; a stone floor shows the paler worn path.
 */
export const TRACK_PALETTE: Readonly<Record<string, TrackPalette>> = Object.freeze({
  earth: { bed: "56,40,22", bedAlpha: 0.36, worn: "204,178,136", wornAlpha: 0.46 },
  sand: { bed: "96,72,46", bedAlpha: 0.28, worn: "236,222,190", wornAlpha: 0.34 },
  snow: { bed: "58,70,92", bedAlpha: 0.42, worn: "140,150,168", wornAlpha: 0.18 },
  ash: { bed: "16,10,8", bedAlpha: 0.52, worn: "120,92,76", wornAlpha: 0.22 },
  mud: { bed: "26,22,14", bedAlpha: 0.46, worn: "116,106,76", wornAlpha: 0.22 },
  stone: { bed: "14,12,20", bedAlpha: 0.36, worn: "150,140,162", wornAlpha: 0.3 },
});

export const trackPalette = (ground: string): TrackPalette =>
  TRACK_PALETTE[ground] ?? TRACK_PALETTE["earth"] as TrackPalette;
